import useGameStore, { pricePerShareOf } from '../store/gameStore';
import { money } from '../lib/format';
import { DISTRICT_META } from './districtMeta';
export const OfferChoiceCard = () => {
  const store = useGameStore();
  const offer = store.pendingOffer;
  const human = store.players[store.currentPlayerIndex];
  if (!offer) return null;
  const tile = store.board.find((t) => t.id === offer.tileId);
  if (!tile || !human || human.isAI) return null;
  const meta = DISTRICT_META[tile.district];
  const float = Math.max(0, tile.totalShares - Object.values(tile.shares).reduce((s, v) => s + v, 0));
  const unit = pricePerShareOf(tile);
  const owned = tile.shares[human.id] ?? 0;
  // Presets only: 50 % control or the whole plot. No 1–100 sliders on a landing.
  const control = Math.max(0, Math.min(float, Math.ceil(tile.totalShares / 2 + 0.01) - owned));
  const whole = Math.max(0, Math.min(float, tile.totalShares - owned));
  const controlCost = Math.round(control * unit);
  const wholeCost = Math.round(whole * unit);
  const buy = (shares: number) => { store.addShares(human.id, tile.id, shares, unit); store.clearOffer(); };
  return (
    <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">Nikdo ho nevlastní · {meta.label}</p>
      <h3 className="mt-1 font-extrabold text-white">{tile.name} — {money(unit)}/akcie</h3>
      <div className="mt-3 grid gap-2">
        <button
          disabled={control <= 0 || human.cash < controlCost}
          onClick={() => buy(control)}
          className="min-h-[52px] w-full rounded-xl bg-gradient-to-r from-cyan-400 to-sky-400 px-4 py-2.5 text-sm font-black text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
        >
          Koupit 50 % ({control} ks) za {money(controlCost)}
        </button>
        {whole > control && (
          <button
            disabled={human.cash < wholeCost}
            onClick={() => buy(whole)}
            className="min-h-[52px] w-full rounded-xl bg-gradient-to-r from-cyan-300 to-emerald-300 px-4 py-2.5 text-sm font-black text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
          >
            Koupit 100 % ({whole} ks) za {money(wholeCost)}
          </button>
        )}
        <button
          onClick={() => { store.clearOffer(); store.openAuction(tile.id, float); }}
          className="min-h-[52px] w-full rounded-xl bg-gradient-to-r from-amber-300 to-orange-400 px-4 py-2.5 text-sm font-black text-slate-950"
        >
          Pustit do aukce (od $50)
        </button>
      </div>
    </div>
  );
};
export const AuctionPanel = () => {
  const store = useGameStore();
  const { auction, players } = store;
  if (!auction.isOpen) return null;
  const tile = store.board.find((t) => t.id === auction.propertyId);
  // Hotseat turn-based bidding: whoever holds the bid slot acts now.
  const currentBidder = players.find((p) => p.id === auction.turnOrder[auction.turnIndex]);
  const leader = players.find((p) => p.id === auction.highestBidderId);
  const seller = auction.sellerId ? players.find((p) => p.id === auction.sellerId) : null;
  const thinker = players.find((p) => p.id === auction.thinkingBidderId);
  const minimum = auction.highestBidderId === null ? auction.currentBid : auction.currentBid + 50;
  const isHumanSlot = Boolean(currentBidder && !currentBidder.isAI);
  const bidButtons = [50, 100, 250];
  // Symbolic opening: with no leader, bids are placed on top of the flat $50 open.
  const bidBase = auction.highestBidderId === null ? Math.min(auction.currentBid, 50) : auction.currentBid;
  const pct = Math.min(100, (auction.timeRemaining / 10) * 100);
  return (
    <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">Auction · {tile?.name}</p>
        {thinker || currentBidder?.isAI ? (
          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] font-bold text-amber-200">{auction.timeRemaining}s</span>
        ) : null}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-orange-400 transition-all duration-1000" style={{ width: `${pct}%` }} /></div>
      <p className="mt-2 text-sm text-slate-200">{auction.shareTranche} shares{seller ? ` from ${seller.name}` : ''} · opening $50 {leader ? <><b className="text-amber-200">{money(auction.currentBid)}</b> <span className="rounded bg-amber-400/20 px-1.5 py-0.5 font-bold text-amber-200">{leader.name} leads</span></> : '· no bids yet'}</p>
      {thinker && <p className="mt-1 text-[11px] text-slate-400"><span className="mr-1 inline-block h-2 w-2 animate-ping rounded-full bg-cyan-300" />{thinker.name} is thinking…</p>}
      <div className="mt-2 max-h-28 space-y-0.5 overflow-auto rounded-xl border border-slate-700/50 bg-slate-950/60 p-2 text-[11px]">
        {auction.bidHistory.length === 0 && <p className="text-slate-500">Waiting for opening bid…</p>}
        {[...auction.bidHistory].reverse().map((b, i) => (
          <p key={`${b.bidderId}-${b.amount}-${i}`} className={i === 0 ? 'font-bold text-amber-200' : 'text-slate-300'}>{b.bidderName} bids {money(b.amount)}{i === 0 ? ' ★' : ''}</p>
        ))}
      </div>
      {isHumanSlot && currentBidder ? (
        <div className="mt-2">
          {/* Sequential handoff: only the player whose bid slot it is acts. */}
          <p className="flex items-center gap-1.5 rounded-lg bg-slate-900/80 px-2 py-1.5 text-[11px] font-bold text-amber-200">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: currentBidder.color }} />
            Current Turn to Bid: {currentBidder.name} · min {money(minimum)}
          </p>
          {/* In-auction emergency borrowing: credit line + quick-draw buttons. */}
          {(() => {
            const credit = Math.max(0, store.debtCeilingOf(currentBidder.id) - currentBidder.debt);
            const maxLoan = Math.min(credit, 1000);
            return (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-lg border border-sky-400/40 bg-sky-500/10 px-2 py-1.5">
                <span className="text-[11px] font-bold text-sky-200">
                  Cash {money(currentBidder.cash)} · Available Credit: {money(credit)}
                </span>
                <div className="ml-auto flex gap-1.5">
                  <button
                    disabled={credit < 250}
                    onClick={() => store.takeLoan(currentBidder.id, 250)}
                    className="min-h-[44px] rounded-lg bg-sky-600/90 px-2 py-1 text-[10px] font-black text-white transition hover:bg-sky-500 disabled:bg-slate-700/60 disabled:text-slate-500"
                  >
                    Borrow +$250
                  </button>
                  <button
                    disabled={credit < 500}
                    onClick={() => store.takeLoan(currentBidder.id, 500)}
                    className="min-h-[44px] rounded-lg bg-sky-600/90 px-2 py-1 text-[10px] font-black text-white transition hover:bg-sky-500 disabled:bg-slate-700/60 disabled:text-slate-500"
                  >
                    Borrow +$500
                  </button>
                  <button
                    disabled={maxLoan <= 0}
                    onClick={() => store.takeLoan(currentBidder.id, maxLoan)}
                    className="min-h-[44px] rounded-lg bg-sky-800/90 px-2 py-1 text-[10px] font-black text-sky-100 transition hover:bg-sky-700 disabled:bg-slate-700/60 disabled:text-slate-500"
                  >
                    Max Loan
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="mt-2 flex gap-2">
            {bidButtons.map((inc) => {
              const amount = bidBase + inc;
              return (
                <button
                  key={inc}
                  disabled={currentBidder.cash < amount}
                  onClick={() => store.placeBid(currentBidder.id, amount)}
                  className="min-h-[48px] flex-1 rounded-lg bg-amber-500/80 py-1.5 text-xs font-bold text-slate-950 disabled:bg-slate-700/60 disabled:text-slate-500"
                >
                  Bid +{money(inc)}
                </button>
              );
            })}
          </div>
          <button onClick={() => store.foldAuction(currentBidder.id)} className="mt-2 w-full rounded-lg bg-slate-800 py-1.5 text-xs font-semibold text-slate-300">
            Pass / Fold — next bidder
          </button>
        </div>
      ) : (
        currentBidder && <p className="mt-2 text-[11px] text-slate-400">Waiting for <b>{currentBidder.name}</b>{currentBidder.isAI ? ' (bot) to act…' : ''}</p>
      )}
    </div>
  );
};
